import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { formatDateTime, formatCurrency } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { UserPlus, Edit, TrendingUp, Users, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const RefereeManagement = () => {
  const { hasRole } = useAuth();
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedReferee, setSelectedReferee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_number: '',
    mobile_number: '',
    email: '',
    address: '',
    bank_account_number: '',
    designation: '',
    entity_type: 'individual',
    role_in_organisation: '',
    commission_percentage: 5,
  });

  const canManage = hasRole(['super_admin', 'admin']);

  useEffect(() => {
    fetchReferees();
  }, []);

  const fetchReferees = async () => {
    try {
      const response = await api.get('/referees');
      setReferees(response.data);
    } catch (error) {
      toast.error('Failed to load referees');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditMode(false);
    setFormData({
      name: '',
      contact_number: '',
      mobile_number: '',
      email: '',
      address: '',
      bank_account_number: '',
      designation: '',
      entity_type: 'individual',
      role_in_organisation: '',
      commission_percentage: 5,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (referee) => {
    setEditMode(true);
    setSelectedReferee(referee);
    setFormData({ ...referee });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/referees/${selectedReferee.id}`, formData);
        toast.success('Referee updated successfully');
      } else {
        await api.post('/referees', formData);
        toast.success('Referee created successfully');
      }
      setDialogOpen(false);
      fetchReferees();
    } catch (error) {
      toast.error('Failed to save referee');
    }
  };

  return (
    <div data-testid="referee-management-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Referee Management</h1>
            <p className="text-slate-500">Manage referral partners and commission tracking</p>
          </div>
          {canManage && (
            <Button className="bg-red-500 hover:bg-red-600 rounded-full" onClick={openAddDialog}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Referee
            </Button>
          )}
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Users className="w-5 h-5 text-red-500" />
              Referral Partners
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">Loading...</div>
            ) : referees.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No referees added yet</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referee Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Commission %</TableHead>
                    <TableHead>Status</TableHead>
                    {canManage && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referees.map((referee) => (
                    <TableRow key={referee.id}>
                      <TableCell className="font-mono text-sm font-medium">
                        {referee.referee_code}
                      </TableCell>
                      <TableCell className="font-medium">{referee.name}</TableCell>
                      <TableCell>
                        <Badge className={referee.entity_type === 'individual' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                          {referee.entity_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>📞 {referee.mobile_number}</div>
                        {referee.email && <div className="text-xs text-slate-500">✉️ {referee.email}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-800 font-semibold">
                          {referee.commission_percentage}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={referee.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}>
                          {referee.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(referee)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => {}}>
                              <TrendingUp className="w-4 h-4 text-green-600" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editMode ? 'Edit Referee' : 'Add New Referee'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Entity Type *</Label>
                <Select value={formData.entity_type} onValueChange={(v) => setFormData({...formData, entity_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="organisation">Organisation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mobile Number *</Label>
                <Input
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                  placeholder="+91-XXXXXXXXXX"
                  required
                />
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input
                  value={formData.contact_number}
                  onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
                  placeholder="Alternate number"
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="referee@example.com"
              />
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Complete address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Designation</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                  placeholder="e.g., Director, Manager"
                />
              </div>
              {formData.entity_type === 'organisation' && (
                <div>
                  <Label>Role in Organisation</Label>
                  <Input
                    value={formData.role_in_organisation}
                    onChange={(e) => setFormData({...formData, role_in_organisation: e.target.value})}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Account Number</Label>
                <Input
                  value={formData.bank_account_number}
                  onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                  placeholder="For commission payments"
                />
              </div>
              <div>
                <Label>Commission Percentage *</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.commission_percentage}
                  onChange={(e) => setFormData({...formData, commission_percentage: parseFloat(e.target.value)})}
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600">
                {editMode ? 'Update' : 'Create'} Referee
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RefereeManagement;
